import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './VideoEditor.css';

interface VideoEditorProps {
  onGenerate: (settings: any) => void;
  isGenerating: boolean;
  settings: {
    prompt: string;
    model: string;
    duration: string;
    resolution: string;
    templateName?: string;
  };
  onPromptChange?: (prompt: string) => void;
}

const VideoEditor: React.FC<VideoEditorProps> = ({ 
  onGenerate, 
  isGenerating, 
  settings,
  onPromptChange
}) => {
  const [localSettings, setLocalSettings] = useState(settings);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showDurationMenu, setShowDurationMenu] = useState(false);
  const [showResolutionMenu, setShowResolutionMenu] = useState(false);
  const [showModelMenu, setShowModelMenu] = useState(false);
  const [modelMenuPosition, setModelMenuPosition] = useState({ top: 0, left: 0 });
  const [isPromptFocused, setIsPromptFocused] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const durationRef = useRef<HTMLDivElement>(null);
  const resolutionRef = useRef<HTMLDivElement>(null);
  const modelRef = useRef<HTMLDivElement>(null);
  const modelAreaRef = useRef<HTMLDivElement>(null);
  const modelMenuRef = useRef<HTMLDivElement>(null);

  // Sync localSettings with parent settings
  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setUploadedImage(imageUrl);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
    
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        const imageUrl = URL.createObjectURL(file);
        setUploadedImage(imageUrl);
      }
    }
  };

  const handlePromptChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newPrompt = event.target.value;
    setLocalSettings({ ...localSettings, prompt: newPrompt });
    if (onPromptChange) {
      onPromptChange(newPrompt);
    }
  };

  const handleModelChange = (modelName: string) => {
    const selectedModel = models.find(m => m.name === modelName);
    if (selectedModel) {
      const firstResolution = Object.keys(selectedModel.resolutions)[0];
      const firstDuration = (selectedModel.resolutions as any)[firstResolution][0];
      setLocalSettings({ 
        ...localSettings, 
        model: modelName,
        resolution: firstResolution,
        duration: firstDuration
      });
    }
  };

  const handleResolutionChange = (resolution: string) => {
    const selectedModel = models.find(m => m.name === localSettings.model);
    if (selectedModel) {
      const firstDuration = (selectedModel.resolutions as any)[resolution][0];
      setLocalSettings({ 
        ...localSettings, 
        resolution,
        duration: firstDuration
      });
    }
  };

  const handleDurationChange = (duration: string) => {
    setLocalSettings({ ...localSettings, duration });
  };

  const handleGenerate = () => {
    onGenerate(localSettings);
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Check model menu (including the portal menu)
      if (modelRef.current && !modelRef.current.contains(target) && 
          modelMenuRef.current && !modelMenuRef.current.contains(target)) {
        setShowModelMenu(false);
      }
      
      // Check duration menu
      if (durationRef.current && !durationRef.current.contains(target)) {
        setShowDurationMenu(false);
      }
      
      // Check resolution menu
      if (resolutionRef.current && !resolutionRef.current.contains(target)) {
        setShowResolutionMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const models = [
    { 
      id: 'wan-2.5-fast', 
      name: 'Wan 2.5 Fast', 
      description: 'Fast video generation with audio',
      resolutions: {
        '720p': ['5s', '10s'],
        '1080p': ['5s', '10s']
      },
      audio: true
    },
    { 
      id: 'wan-2.5', 
      name: 'Wan 2.5', 
      description: 'High-quality video generation with audio',
      resolutions: {
        '480p': ['5s', '10s'],
        '720p': ['5s', '10s'],
        '1080p': ['5s', '10s']
      },
      audio: true
    },
    { 
      id: 'sora-2', 
      name: 'Sora 2', 
      description: "OpenAI's advanced video model",
      resolutions: {
        'Portrait 720p': ['4s', '8s', '12s'],
        'Landscape 720p': ['4s', '8s', '12s']
      },
      audio: true
    },
    { 
      id: 'sora-2-pro', 
      name: 'Sora 2 Pro', 
      description: "OpenAI's most advanced video model",
      resolutions: {
        'Portrait 720p': ['4s', '8s', '12s'],
        'Landscape 720p': ['4s', '8s', '12s'],
        'Portrait 1080p': ['4s', '8s', '12s'],
        'Landscape 1080p': ['4s', '8s', '12s']
      },
      audio: true
    },
    { 
      id: 'kling-2.5', 
      name: 'Kling 2.5', 
      description: 'Smooth motion, strong consistency',
      resolutions: {
        '1080p': ['5s', '10s']
      },
      audio: false
    },
    { 
      id: 'seedance-1.0', 
      name: 'Seedance 1.0', 
      description: 'Fast multi-shot video generation',
      resolutions: {
        '720p': ['5s', '10s'],
        '1080p': ['5s', '10s']
      },
      audio: false
    },
    { 
      id: 'seedance-1.0-pro', 
      name: 'Seedance 1.0 Pro', 
      description: 'Professional video generation',
      resolutions: {
        '1080p': ['5s', '10s']
      },
      audio: false
    },
    { 
      id: 'minimax-hailuo-02', 
      name: 'Minimax Hailuo 02', 
      description: 'Dynamic video generation',
      resolutions: {
        '512p': ['6s', '10s'],
        '768p': ['6s', '10s'],
        '1080p': ['6s', '10s']
      },
      audio: false
    }
  ];

  // Get current model's available resolutions and durations
  const currentModel = models.find(m => m.name === localSettings.model) || models[0];
  const availableResolutions = Object.keys(currentModel.resolutions);
  const availableDurations: string[] = (currentModel.resolutions as any)[localSettings.resolution] || Object.values(currentModel.resolutions)[0];


  return (
    <div className="video-editor">
      {/* Upload Image Section */}
      <div className="upload-section">
        <div 
          className={`upload-area ${isDragOver ? 'drag-over' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {uploadedImage ? (
            <div className="uploaded-image">
              <img src={uploadedImage} alt="Uploaded" />
              <div className="image-overlay">
                <button 
                  className="change-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                >
                  Change
                </button>
              </div>
            </div>
          ) : (
            <div className="upload-placeholder">
              <div className="upload-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M17 8L12 3L7 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 3V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="upload-text">Upload Image</span>
              <span className="drag-hint">Drag image here or click to upload</span>
            </div>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          style={{ display: 'none' }}
        />
      </div>

      {/* Prompt Section */}
      <div className="prompt-section">
        <div className="prompt-container">
          <div className="prompt-header">
            <div className="prompt-header-top">
              <span className="prompt-label">Prompt</span>
              {settings.templateName && (
                <span className="template-indicator">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
                  </svg>
                  {settings.templateName}
                </span>
              )}
            </div>
            {!isPromptFocused && !localSettings.prompt && (
              <span className="prompt-hint">Describe your vision in detail</span>
            )}
          </div>
          <textarea
            value={localSettings.prompt}
            onChange={handlePromptChange}
            onFocus={() => setIsPromptFocused(true)}
            onBlur={() => setIsPromptFocused(false)}
            placeholder=""
            className="prompt-textarea"
            rows={4}
          />
          <div className="enhance-section">
            <button className="enhance-btn">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" fill="currentColor"/>
                <path d="M19 15L20.09 21.26L27 22L20.09 22.74L19 29L17.91 22.74L11 22L17.91 21.26L19 15Z" fill="currentColor"/>
                <path d="M5 15L6.09 21.26L13 22L6.09 22.74L5 29L3.91 22.74L-3 22L3.91 21.26L5 15Z" fill="currentColor"/>
              </svg>
              <span>Enhance</span>
            </button>
          </div>
        </div>
      </div>

      {/* Model Selection */}
      <div className="model-section" ref={modelRef}>
        <div 
          ref={modelAreaRef}
          className="model-area"
          onClick={() => {
            if (!showModelMenu && modelAreaRef.current) {
              const rect = modelAreaRef.current.getBoundingClientRect();
              const menuHeight = 500; // 估计的菜单高度
              const viewportHeight = window.innerHeight;
              
              // 计算最佳位置：优先向上显示，确保不被截断
              let topPosition = rect.top - menuHeight;
              
              // 如果向上超出屏幕，则调整到可见范围内
              if (topPosition < 20) {
                topPosition = 20;
              }
              
              // 如果向下超出屏幕，则向上调整
              if (topPosition + menuHeight > viewportHeight - 20) {
                topPosition = viewportHeight - menuHeight - 20;
              }
              
              setModelMenuPosition({
                top: topPosition,
                left: rect.right + 8
              });
            }
            setShowModelMenu(!showModelMenu);
          }}
        >
          <span className="model-text">Model</span>
          <span className="model-value">{localSettings.model}</span>
        </div>
        {showModelMenu && createPortal(
          <div 
            ref={modelMenuRef}
            className="model-menu" 
            style={{ 
              top: `${modelMenuPosition.top}px`, 
              left: `${modelMenuPosition.left}px` 
            }}
          >
            {models.map((model) => (
              <div
                key={model.id}
                className={`model-option ${localSettings.model === model.name ? 'selected' : ''}`}
                onClick={() => {
                  handleModelChange(model.name);
                  setShowModelMenu(false);
                }}
              >
                <div className="model-option-header">
                  <div className="model-option-title">
                    <span className="model-option-name">{model.name}</span>
                  </div>
                  {localSettings.model === model.name && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
                <div className="model-option-description">{model.description}</div>
              </div>
            ))}
          </div>,
          document.body
        )}
      </div>

      {/* Resolution and Duration Section */}
      <div className="resolution-duration-row">
        {/* Resolution Selection */}
        <div className="resolution-section" ref={resolutionRef}>
          <div 
            className="resolution-area"
            onClick={() => setShowResolutionMenu(!showResolutionMenu)}
          >
            <span className="resolution-text">Resolution</span>
            <span className="resolution-value">{localSettings.resolution}</span>
          </div>
          {showResolutionMenu && (
            <div className="resolution-menu">
              {availableResolutions.map((resolution) => (
                <div
                  key={resolution}
                  className={`resolution-option ${localSettings.resolution === resolution ? 'selected' : ''}`}
                  onClick={() => {
                    handleResolutionChange(resolution);
                    setShowResolutionMenu(false);
                  }}
                >
                  {resolution}
                  {localSettings.resolution === resolution && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Duration Selection */}
        <div className="duration-section" ref={durationRef}>
          <div 
            className="duration-area"
            onClick={() => setShowDurationMenu(!showDurationMenu)}
          >
            <span className="duration-text">Duration</span>
            <span className="duration-value">{localSettings.duration}</span>
          </div>
          {showDurationMenu && (
            <div className="duration-menu">
              {availableDurations.map((duration) => (
                <div
                  key={duration}
                  className={`duration-option ${localSettings.duration === duration ? 'selected' : ''}`}
                  onClick={() => {
                    handleDurationChange(duration);
                    setShowDurationMenu(false);
                  }}
                >
                  {duration}
                  {localSettings.duration === duration && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Generate Button */}
      <div className="generate-section">
        <button 
          className={`generate-btn ${isGenerating ? 'generating' : ''}`}
          onClick={handleGenerate}
          disabled={isGenerating}
        >
          {isGenerating ? (
            <>
              <div className="loading-spinner"></div>
              Generating...
            </>
          ) : (
            'Generate'
          )}
        </button>
      </div>
    </div>
  );
};

export default VideoEditor;

